import { ClassList } from 'lib/utils/react/classlist';
import React, { DetailedHTMLProps, FormHTMLAttributes, PropsWithChildren } from 'react';
import { FieldErrors, get, useFormContext } from 'react-hook-form';
import { InputDescription } from './descriptions';
import { InputErrors } from './error-message';

export const DefaultLayoutForm: React.FC<
  DetailedHTMLProps<FormHTMLAttributes<HTMLFormElement>, HTMLFormElement> &
    PropsWithChildren
> = ({ children, ...formProps }) => {
  return (
    <form {...formProps} className="default-layout">
      {children}
    </form>
  );
};

export const FormHorizontalGroup: React.FC<PropsWithChildren> = ({ children }) => {
  return <div className="horizontal-group">{children}</div>;
};

export const FormElement: React.FC<
  {
    description?: string;
    errors?: string[];
    variant?: 'stretched' | 'unstretched';
  } & PropsWithChildren
> = ({ description, errors, variant = 'stretched', children }) => {
  const isInvalid = !!errors?.length;

  const classes = ClassList.of('form-element');
  classes.add('is-' + variant);
  classes.add('is-invalid', isInvalid);

  return (
    <div className={classes.names}>
      {children}
      {errors ? (
        <p className="form-element-errors">
          {errors
            .map((error) =>
              !error.endsWith('.') && !error.endsWith('!') ? error + '.' : error
            )
            .join(' ')}
        </p>
      ) : null}
      {description ? <p className="form-element-description">{description}</p> : null}
    </div>
  );
};

export const ControlledFormElement: React.FC<
  {
    name: string;
    description?: string;
    errors: FieldErrors;
    variant?: 'stretched' | 'unstretched';
  } & PropsWithChildren
> = ({ description, name, errors, variant = 'stretched', children }) => {
  // Check for errors
  const methods = useFormContext();
  const error = get(errors || methods.formState.errors, name);
  const isInvalid = error !== undefined;

  // Build class name list
  const classes = ClassList.of('form-element');
  classes.add('is-' + variant);
  classes.add('is-invalid', isInvalid);

  return (
    <div className={classes.names}>
      {children}
      <InputDescription description={description} />
      <InputErrors name={name} errors={errors} />
    </div>
  );
};

export const FormButtons: React.FC<
  { errors?: string[]; isRemoveMargin?: boolean } & PropsWithChildren
> = ({ isRemoveMargin = false, errors, children }) => {
  const isInvalid = !!errors?.length;

  const classes = ClassList.of('form-buttons');
  classes.add('is-invalid', isInvalid);
  classes.add('is-no-margin', isRemoveMargin);

  return (
    <div className={classes.names}>
      {children}
      {errors ? (
        <p className="form-element-errors">
          {errors
            .map((error) =>
              !error.endsWith('.') && !error.endsWith('!') ? error + '.' : error
            )
            .join(' ')}
        </p>
      ) : null}
    </div>
  );
};
